import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Home, Store, ShoppingCart, UtensilsCrossed, ChefHat, Users, Smartphone, DollarSign, BarChart3, Settings, LogOut, Menu, X, Bike, PackageCheck, Tag, Map } from "lucide-react";
import { vpAxios } from "./api";
import useVaipedeManifest from "./useVaipedeManifest";
import { TrialBanner, TrialExpiredModal } from "./TrialBanner";
import HomePage from "./pages/HomePage";
import MesasPage from "./pages/MesasPage";
import PdvPage from "./pages/PdvPage";
import ProdutosPage from "./pages/ProdutosPage";
import CozinhaPage from "./pages/CozinhaPage";
import ClientesPage from "./pages/ClientesPage";
import CardapioPedidosPage from "./pages/CardapioPedidosPage";
import CaixaPage from "./pages/CaixaPage";
import RelatoriosPage from "./pages/RelatoriosPage";
import ConfigPage from "./pages/ConfigPage";
import TaxasEntregaPage from "./pages/TaxasEntregaPage";
import EntregasPage from "./pages/EntregasPage";
import EntregadoresPage from "./pages/EntregadoresPage";
import GarconsPage from "./pages/GarconsPage";
import CuponsPage from "./pages/CuponsPage";

const NAV = [
  { path: "", label: "Início", icon: Home, exact: true },
  { path: "mesas", label: "Mesas", icon: Store },
  { path: "pdv", label: "PDV / Pedido", icon: ShoppingCart },
  { path: "produtos", label: "Produtos", icon: UtensilsCrossed },
  { path: "cozinha", label: "Cozinha", icon: ChefHat },
  { path: "entregas", label: "Entregas", icon: PackageCheck },
  { path: "entregadores", label: "Entregadores", icon: Bike },
  { path: "garcons", label: "Garçons", icon: UtensilsCrossed },
  { path: "clientes", label: "Clientes", icon: Users },
  { path: "cardapio-pedidos", label: "Cardápio Digital", icon: Smartphone },
  { path: "cupons", label: "Cupons e Promoções", icon: Tag },
  { path: "caixa", label: "Caixa", icon: DollarSign },
  { path: "entrega-taxas", label: "Taxas de Entrega", icon: Map },
  { path: "relatorios", label: "Relatórios", icon: BarChart3 },
  { path: "config", label: "Configurações", icon: Settings },
];

export default function VaiPedeDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendCount, setPendCount] = useState(0);
  useVaipedeManifest();

  useEffect(() => {
    const token = localStorage.getItem("vaipede_token");
    if (!token) {
      navigate("/vaipede/login", { replace: true });
      return;
    }
    vpAxios.get("/vaipede/auth/me")
      .then(({ data }) => {
        setUser(data);
        localStorage.setItem("vaipede_user", JSON.stringify(data));
      })
      .catch(() => {
        localStorage.removeItem("vaipede_token");
        navigate("/vaipede/login", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  // Polling de badge de pedidos aguardando
  useEffect(() => {
    const load = () => vpAxios.get("/vaipede/pedidos?origem=cardapio_digital&status=aguardando")
      .then(r => setPendCount((r.data || []).length))
      .catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const logout = () => {
    localStorage.removeItem("vaipede_token");
    localStorage.removeItem("vaipede_user");
    navigate("/vaipede/login");
  };

  const currentPath = location.pathname.replace(/^\/vaipede\/dashboard\/?/, "");
  const isActive = (p) => (p === "" ? currentPath === "" || currentPath === "/" : currentPath.startsWith(p));

  if (loading) return <div className="min-h-screen grid place-items-center bg-neutral-50">Carregando VAI PEDE...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="vaipede-dashboard">
      <TrialBanner user={user}/>
      <TrialExpiredModal/>
      {/* Mobile top bar */}
      <div className="md:hidden bg-white border-b border-neutral-200 px-4 h-14 flex items-center justify-between sticky top-0 z-30">
        <button onClick={() => setSideOpen(true)} data-testid="vaipede-menu-toggle">
          <Menu className="w-6 h-6"/>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg grid place-items-center bg-[#EA1D2C] text-white font-black text-sm">V</div>
          <span className="font-black">VAI <span className="text-[#EA1D2C]">PEDE</span></span>
        </div>
        <div className="w-6"/>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-neutral-900 text-white transform transition-transform ${sideOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="p-5 flex items-start justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#EA1D2C] text-white font-black shrink-0">V</div>
            <div className="min-w-0">
              <div className="font-black text-lg tracking-tight leading-none">VAI <span className="text-[#EA1D2C]">PEDE</span></div>
              <div className="text-[11px] text-neutral-400 truncate mt-0.5" data-testid="vaipede-sidebar-loja-nome">
                {user.loja_nome || user.nome}
              </div>
            </div>
          </div>
          <button className="md:hidden shrink-0" onClick={() => setSideOpen(false)}><X className="w-5 h-5"/></button>
        </div>
        <nav className="px-3 space-y-1">
          {NAV.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badge = item.path === "cardapio-pedidos" && pendCount > 0 ? pendCount : null;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(`/vaipede/dashboard/${item.path}`); setSideOpen(false); }}
                data-testid={`vaipede-nav-${item.path || "home"}`}
                className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${active ? "bg-[#EA1D2C] text-white" : "text-neutral-300 hover:bg-white/10"}`}
              >
                <Icon className="w-4 h-4"/>
                {item.label}
                {badge && <span className="ml-auto bg-red-500 text-white text-xs font-black rounded-full min-w-[20px] h-5 grid place-items-center px-1 animate-pulse" data-testid="vaipede-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>
        <div className="absolute bottom-4 left-4 right-4 border-t border-white/10 pt-3">
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-neutral-300 hover:bg-white/10" data-testid="vaipede-logout">
            <LogOut className="w-4 h-4"/> Sair
          </button>
        </div>
      </aside>

      {sideOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setSideOpen(false)}/>}

      {/* Content */}
      <main className="md:pl-60">
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          <Routes>
            <Route index element={<HomePage/>}/>
            <Route path="mesas" element={<MesasPage/>}/>
            <Route path="pdv" element={<PdvPage/>}/>
            <Route path="produtos" element={<ProdutosPage/>}/>
            <Route path="cozinha" element={<CozinhaPage/>}/>
            <Route path="entregas" element={<EntregasPage/>}/>
            <Route path="entregadores" element={<EntregadoresPage/>}/>
            <Route path="garcons" element={<GarconsPage/>}/>
            <Route path="clientes" element={<ClientesPage/>}/>
            <Route path="cardapio-pedidos" element={<CardapioPedidosPage/>}/>
            <Route path="cupons" element={<CuponsPage/>}/>
            <Route path="caixa" element={<CaixaPage/>}/>
            <Route path="entrega-taxas" element={<TaxasEntregaPage/>}/>
            <Route path="relatorios" element={<RelatoriosPage/>}/>
            <Route path="config" element={<ConfigPage/>}/>
            <Route path="*" element={<Navigate to="" replace/>}/>
          </Routes>
        </div>
      </main>
    </div>
  );
}
